import React, { useCallback, useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { ChevronLeft, ChevronRight, Trophy } from 'lucide-react';
import Spinner from '../../components/UI/Spinner';
import EmptyState from '../../components/UI/EmptyState';
import { AddressLink, StatTile, TierBadge, btc, usd } from './components';
import { USDT_DECIMALS, WBTC_DECIMALS } from './config';
import { useT } from '../../i18n';

const toNum = (value, decimals) =>
  parseFloat(ethers.utils.formatUnits(value.toString(), decimals));

/**
 * Winners for any resolved round. Rounds are addressed by id, so the browser is
 * a simple stepper rather than a paged list, because the contract has no index of
 * rounds that produced winners.
 */
const Winners = ({ game }) => {
  const t = useT();
  const { contract, liveRoundId, prevStats } = game;

  const [roundId, setRoundId] = useState(null);
  const [winners, setWinners] = useState([]);
  const [finalPrice, setFinalPrice] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default to the most recently resolved round.
  useEffect(() => {
    if (roundId === null && prevStats && prevStats.roundId) {
      setRoundId(prevStats.roundId);
    }
  }, [prevStats, roundId]);

  const load = useCallback(
    async (id) => {
      if (!id) return;
      setLoading(true);
      try {
        const [list, price] = await Promise.all([
          contract.getRoundWinners(id).catch(() => []),
          contract.finalPrices(id).catch(() => null),
        ]);

        setWinners(
          (list || []).map((winner) => ({
            player: winner.player,
            price: Number(winner.predictedPrice.toString()),
            usdt: toNum(winner.usdtPrize, USDT_DECIMALS),
            wbtc: toNum(winner.wbtcPrize, WBTC_DECIMALS),
            tier: Number(winner.tier.toString()),
          }))
        );
        setFinalPrice(price ? Number(price.toString()) : null);
      } catch (err) {
        console.error('[BullsEye] winners read failed', err);
        setWinners([]);
        setFinalPrice(null);
      } finally {
        setLoading(false);
      }
    },
    [contract]
  );

  useEffect(() => {
    load(roundId);
  }, [load, roundId]);

  const maxRound = Math.max(liveRoundId - 1, 1);
  const totals = winners.reduce(
    (acc, winner) => ({ usdt: acc.usdt + winner.usdt, wbtc: acc.wbtc + winner.wbtc }),
    { usdt: 0, wbtc: 0 }
  );

  return (
    <div className="space-y-6">
      <section className="lb-section p-6 sm:p-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <span className="lb-chip lb-chip-gold">
              <Trophy className="h-3.5 w-3.5" aria-hidden="true" />
              {t('bullseye.win.results')}
            </span>
            <h2 className="lb-heading mt-4 text-4xl sm:text-5xl">{t('bullseye.win.roundWinners')}</h2>
            <p className="mt-3 max-w-xl text-sm text-white/70">
              {t('bullseye.win.sub')}
            </p>
          </div>

          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={() => setRoundId((id) => Math.max(1, (id || 1) - 1))}
              disabled={!roundId || roundId <= 1}
              className="lb-btn lb-btn-ghost lb-btn-sm"
              aria-label={t('bullseye.previousRound')}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>

            <div>
              <label className="lb-label mb-1.5 block" htmlFor="bullseye-round">
                {t('bullseye.round')}
              </label>
              <input
                id="bullseye-round"
                type="number"
                min="1"
                max={maxRound}
                value={roundId || ''}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  setRoundId(Number.isFinite(value) && value > 0 ? value : null);
                }}
                className="lb-input notranslate lb-num w-28 text-center"
              />
            </div>

            <button
              type="button"
              onClick={() => setRoundId((id) => Math.min(maxRound, (id || 1) + 1))}
              disabled={!roundId || roundId >= maxRound}
              className="lb-btn lb-btn-ghost lb-btn-sm"
              aria-label={t('bullseye.nextRound')}
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile label={t('bullseye.round')} value={roundId ? `#${roundId}` : '-'} />
          <StatTile
            label={t('bullseye.winningPrice')}
            value={finalPrice ? `$${usd(finalPrice)}` : 'Not resolved'}
            tone="gold"
          />
          <StatTile label={t('bullseye.winners')} value={usd(winners.length)} />
          <StatTile
            label={t('bullseye.paidOut')}
            value={`${usd(totals.usdt, 2)} USDT`}
            sub={totals.wbtc > 0 ? `+ ${btc(totals.wbtc)} WBTC` : null}
            tone="gradient"
          />
        </div>
      </section>

      <section className="lb-card p-5 sm:p-6">
        {loading ? (
          <div className="py-16">
            <Spinner label={t('bullseye.win.loading')} />
          </div>
        ) : winners.length ? (
          <div className="lb-scroll overflow-x-auto">
            <table className="w-full min-w-[560px] text-left text-sm">
              <thead>
                <tr className="text-[0.65rem] uppercase tracking-[0.16em] text-white/45">
                  <th className="px-3 py-3 font-normal">#</th>
                  <th className="px-3 py-3 font-normal">{t('bullseye.player')}</th>
                  <th className="px-3 py-3 font-normal">{t('bullseye.prediction')}</th>
                  <th className="px-3 py-3 font-normal">{t('bullseye.tier')}</th>
                  <th className="px-3 py-3 text-right font-normal">{t('bullseye.usdtPrize')}</th>
                  <th className="px-3 py-3 text-right font-normal">{t('bullseye.wbtcPrize')}</th>
                </tr>
              </thead>
              <tbody>
                {winners.map((winner, index) => (
                  <tr
                    key={`${winner.player}-${index}`}
                    className="border-t border-white/5 text-white/80 transition hover:bg-white/[0.03]"
                  >
                    <td className="notranslate lb-num px-3 py-3 text-white/40">{index + 1}</td>
                    <td className="px-3 py-3">
                      <AddressLink value={winner.player} />
                    </td>
                    <td className="notranslate lb-num px-3 py-3 font-bold text-white">
                      ${usd(winner.price)}
                    </td>
                    <td className="px-3 py-3">
                      <TierBadge tier={winner.tier} />
                    </td>
                    <td className="notranslate lb-num px-3 py-3 text-right font-bold text-white">
                      {usd(winner.usdt, 2)}
                    </td>
                    <td className="notranslate lb-num px-3 py-3 text-right text-amber-300">
                      {winner.wbtc > 0 ? btc(winner.wbtc) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={Trophy}
            title={t('bullseye.win.none')}
            message={
              finalPrice
                ? t('bullseye.win.noneInBand')
                : t('bullseye.win.pending')
            }
          />
        )}
      </section>
    </div>
  );
};

export default Winners;
